import { useEffect, useState, Suspense, lazy } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Transition } from 'semantic-ui-react'
import { useQuery, useMutation } from '@apollo/client'
import * as DOMPurify from 'dompurify'

import { Container, Modal } from '@/ui'
import { StyledToastContainer } from '@/ui/toast/Styles'
import GlobalStyles from '@/ui/global'
import format from '@/helpers/format'
import ConfirmationServiceProvider from '@/components/ConfirmComponent/ConfirmationServiceProvider'
import {
  useRouter,
  useLoggedInUser,
  useCompanySetting,
  useBreakpoints,
  useUserSetting,
} from '@/hooks'
import {
  LATEST_POPUP_ANNOUNCEMENT,
  UNREAD_ANNOUNCEMENTS_COUNT,
} from '@/graphql/queries/Announcement'
import { FILE_ID_TO_URL } from '@/graphql/queries/Storage'
import { READ_ANNOUNCEMENT } from '@/graphql/mutations/Announcement'
import { batchClient } from '@/components/App/client'
import useTitle from '@/hooks/useTitle'
import { createUnreadCountVariables } from './TopBar/News/query-variables'

const ModalComponent = lazy(() => import('./ModalComponent'))
const TopBar = lazy(() => import('./TopBar'))
const Sidebar = lazy(() => import('./Sidebar'))
const Powerbar = lazy(() => import('./Powerbar'))
const ErrorBoundary = lazy(() => import('@/components/ErrorBoundary'))

const mainMenuWidth = 65
const pagePadding = 25

const StyledContainer = styled(({ fullWidth, ...rest }) => (
  <Container {...rest} />
))`
  //TODO: rewrite this styling. I think it is some leftovers from when the navigation was at the top of the screen
  ${({ fullWidth, theme }) => css`
    &&& {
      max-width: 100% !important;
      padding-left: ${fullWidth
        ? mainMenuWidth
        : mainMenuWidth + pagePadding}px !important;
      padding-right: ${fullWidth ? 0 : pagePadding}px !important;
      padding-top: 80px;
      margin-bottom: 1rem;
    }

    ${theme.mediaQueries.mobile} {
      &&& {
        max-width: 100% !important;
        padding: 1em !important;
        padding-top: 70px !important;
      }
    }
  `}
`

const MainContainer = ({ children }: { children: React.ReactNode }) => {
  //remove this component, when StyledContainer has been rewritten
  const { location } = useRouter()

  const path = location?.pathname.split('/')[1]

  const isEmployeePage = location?.pathname.includes('/employee/')

  // addemployee doens't need fullWidth, because the submenu sidebar is not rendered there
  const isEmployeesPage =
    path.includes('employees') && !location?.pathname.includes('/addemployee')

  return (
    <StyledContainer
      fluid
      fullWidth={
        isEmployeePage ||
        isEmployeesPage ||
        ['competences', 'documents', 'settings', 'vacation', 'pulse'].includes(
          path,
        )
      }>
      {children}
    </StyledContainer>
  )
}

const Navigation = () => {
  const { isMobile } = useBreakpoints()

  return (
    <>
      <TopBar />
      {!isMobile && <Sidebar isMobile={isMobile} />}
    </>
  )
}

const LatestAnnouncement = () => {
  const [currentDate] = useState(format(new Date(), 'yyyy-MM-dd') ?? '')

  const [open, setOpen] = useState(true)

  const { userId, locale, originalUserId } = useLoggedInUser()

  const { data } = useQuery(LATEST_POPUP_ANNOUNCEMENT, {
    variables: {
      locale,
      currentDate,
    },
    skip: !!originalUserId,
    client: batchClient,
  })

  const [readAnnouncement] = useMutation(READ_ANNOUNCEMENT)

  const announcement = data?.announcements?.nodes?.[0]

  const hasRead = announcement?.userAnnouncements?.nodes?.some?.(
    node => node?.userId === userId,
  )

  if (!announcement || hasRead || !!originalUserId) {
    return null
  }

  const { id, title, content } = announcement

  const handleClose = async () => {
    setOpen(false)

    readAnnouncement({
      variables: {
        announcementId: id,
        userId,
      },
      refetchQueries: [
        {
          query: UNREAD_ANNOUNCEMENTS_COUNT,
          variables: createUnreadCountVariables(locale, currentDate),
        },
      ],
    })
  }

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header>{title}</Modal.Header>

      <Modal.Content image>
        <Modal.Description
          style={{ width: '100%' }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.default.sanitize(content ?? '', {
              ADD_TAGS: ['iframe'],
            }),
          }}
        />
      </Modal.Content>
    </Modal>
  )
}

const App = ({
  children,
  unauthenticated,
}: {
  children: React.ReactNode
  unauthenticated?: boolean
}) => {
  const { t } = useTranslation()
  const { location } = useRouter()
  useTitle()

  const [{ value: timeZone }, setTimeZone] = useUserSetting('TIME_ZONE')

  useEffect(() => {
    if (!timeZone && typeof setTimeZone === 'function') {
      setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
    }
  }, [timeZone, setTimeZone])

  const [favicon] = useCompanySetting('FAVICON')

  const { data: faviconData } = useQuery(FILE_ID_TO_URL, {
    skip: !favicon,
    variables: {
      id: favicon?.id,
    },
    client: batchClient,
  })

  const faviconElem = document.getElementById(
    'favicon',
  ) as HTMLLinkElement | null
  if (faviconElem && faviconData?.storageIdToDirectUrl?.url) {
    faviconElem.href = faviconData?.storageIdToDirectUrl?.url
  }
  // Always compare with the root/base path
  const rootPath = location.pathname.split('/')[1]
  if (
    unauthenticated ||
    [
      '',
      'login',
      'sign-login',
      'sign-documents',
      'engagement-login',
      'engagement-form',
      'pulse-survey-login',
      'submit-pulse-survey',
    ].includes(rootPath)
  ) {
    return (
      <Suspense>
        <ConfirmationServiceProvider key="unauthed" t={t}>
          <StyledToastContainer position={toast.POSITION.BOTTOM_RIGHT} />
          <GlobalStyles />
          {children}
          <ModalComponent />
        </ConfirmationServiceProvider>
      </Suspense>
    )
  }

  return (
    <Suspense>
      <ConfirmationServiceProvider key="authed" t={t}>
        <StyledToastContainer position={toast.POSITION.BOTTOM_RIGHT} />
        <GlobalStyles />
        <LatestAnnouncement />

        <Powerbar />
        <Transition visible transitionOnMount duration={300}>
          <div>
            <Navigation />

            <MainContainer>
              <ErrorBoundary location={location}>{children}</ErrorBoundary>
            </MainContainer>
          </div>
        </Transition>

        <ModalComponent />
      </ConfirmationServiceProvider>
    </Suspense>
  )
}

export default App
